import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
// Direct @ant-design/icons exception: ui-components' <Icon> has no spin
// support yet. Remove once spin lands in @timeedit/ui-components v7.
import { Loading3QuartersOutlined } from '@ant-design/icons';
import DiscardConfirmation, {
  useDiscardConfirmation,
} from 'components/DiscardConfirmation';
import {
  ObjectTable,
  type ObjectTableProps,
} from 'components/ObjectTable/ObjectTable';
import { ObjectTypeTreePopover } from 'components/ObjectTypeTreePopover';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useObjectExportEnabled } from 'hooks/useFeatureFlag';
import { useGlobalFieldOrder } from 'hooks/useGlobalFieldOrder';
import { useTableSelection } from 'hooks/useTableSelection';
import intl, { getText } from 'i18n/intl';
import { fetchAvailabilityRelations } from 'slices/availabilityRelation.slice';
import {
  fetchFields,
  fieldMappingSelector,
  fieldsLoading,
} from 'slices/field.slice';
import { fetchMemberships } from 'slices/memberships.slice';
import {
  checkObjectSaveDisabled,
  createUnsavedObject,
  deleteObject,
  duplicateObject,
  fetchMoreObjectSuccess,
  fetchObjectsFailure,
  fetchObjectsRequest,
  fetchObjectsSuccess,
  findMoreObjects,
  findObjectPayloadSelector,
  firstObjectSelector,
  objectsSelector,
  secondObjectSelector,
  setFindObjectPayload,
  setFirstObject,
  setSecondObject,
} from 'slices/object.slice';
import {
  requestObjectTypes,
  selectObjectTypesList,
  selectObjectTypesLoading,
  selectObjectTypesMap,
} from 'slices/objectTypes.slice';
import { fetchOrganizationHierarchy } from 'slices/organizationHierarchy.slice';
import { fetchRelationTypes } from 'slices/relationType.slice';
import { fetchReservationTemplates } from 'slices/reservationTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { isFieldSaveable } from 'utils/fieldUtils';
import { validateInternalId } from 'utils/internalId.validation';
import { validateObjectId } from 'utils/objectId.validation';
import type { TTEObject, TType } from '@timeedit/types';
import { Button, DefaultContentLayout, Icon } from '@timeedit/ui-components';
import { Popover, Typography } from '@timeedit/ui-components/antd';
import ObjectManagerDrawer from './ObjectManagerDrawer';
import BulkDeleteObjectsJobButton from './components/BulkDeleteObjectsJobButton';
import BulkEditStartJobButton from './components/BulkEditStartJobButton';
import ExportObjectsJobButton from './components/ExportObjectsJobButton';
import { JobResults } from './components/JobResults';
import { useFetchJobQueueStatusQuery } from './components/JobResults/api/objectJobApi';
import { useJobPollingInterval } from './components/JobResults/hooks/useJobPollingInterval';
import ImportObjectsButton from './components/import/ImportObjectsButton';
import { useOperationPermission } from './hooks/useOperationPermission';
import './index.scss';

const language: any = intl.messages;

const ObjectManagerPage = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultFilter = useMemo(() => {
    const organization = searchParams.get('orgNodeId');
    const objectType = searchParams.get('typeId');
    return {
      organization:
        organization && validateObjectId(organization)
          ? organization
          : undefined,
      objectType:
        objectType && validateInternalId(objectType) ? objectType : undefined,
    };
  }, [searchParams]);

  useEffect(() => {
    dispatch(fetchMemberships());
    dispatch(fetchRelationTypes());
    dispatch(fetchAvailabilityRelations());
    dispatch(fetchReservationTemplates());
    dispatch(fetchFields());
    dispatch(fetchOrganizationHierarchy());
    dispatch(requestObjectTypes());
  }, [dispatch]);

  const objects = useAppSelector(objectsSelector);
  const objectTypesMap = useAppSelector(selectObjectTypesMap);
  const fieldMap = useAppSelector(fieldMappingSelector);
  const firstObject = useAppSelector(firstObjectSelector);
  const secondObject = useAppSelector(secondObjectSelector);
  const findObjectPayload = useAppSelector(findObjectPayloadSelector);
  const allObjectTypes = useAppSelector(selectObjectTypesList);
  const objectTypeId = findObjectPayload?.typeId;

  const isLoadingObjects = useAppSelector((state) => state.objects.loading);
  const isLoadingFields = useAppSelector(fieldsLoading);
  const isLoadingObjectTypes = useAppSelector(selectObjectTypesLoading);
  const objectExportEnabled = useObjectExportEnabled();

  const { globalFieldOrder } = useGlobalFieldOrder();
  const hasExportPermission = useOperationPermission('EXPORT');
  const hasDeletePermission = useOperationPermission('DELETE');
  const [isJobPopoverOpen, setIsJobPopoverOpen] = useState(false);
  const { pollingInterval, pollingActive, setPollingActive } =
    useJobPollingInterval();
  const { data: jobQueueStatus, refetch: refetchJobQueueStatus } =
    useFetchJobQueueStatusQuery(undefined, {
      pollingInterval,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    });

  const [showReloadAlert, setShowReloadAlert] = useState(false);
  const [previousLastFinishedAt, setPreviousLastFinishedAt] = useState<
    string | undefined
  >(undefined);

  const lastFinishedAt = jobQueueStatus?.lastFinishedMutationJob?.at;

  if (lastFinishedAt != null && previousLastFinishedAt == null) {
    setPreviousLastFinishedAt(lastFinishedAt);
  } else if (
    lastFinishedAt != null &&
    previousLastFinishedAt !== lastFinishedAt
  ) {
    setPreviousLastFinishedAt(lastFinishedAt);
    setShowReloadAlert(true);
  }

  const runningJobs = jobQueueStatus?.running ?? 0;
  const waitingJobs = jobQueueStatus?.waiting ?? 0;

  const hasJobs = runningJobs + waitingJobs > 0;

  if (hasJobs != pollingActive) {
    setPollingActive(hasJobs);
  }

  const [totalCount, setTotalCount] = useState(0);
  const [selectAllTotal, setSelectAllTotal] = useState(0);

  const [
    isDiscardDialogOpen,
    onCloseDiscardDialog,
    onConfirmDiscardDialog,
    confirmDiscardChanged,
  ] = useDiscardConfirmation();

  const confirmDiscardChanges = useCallback(
    (isFirstDrawer: boolean, onDiscard: () => void) => {
      const isObjectSaveDisabled = dispatch(
        checkObjectSaveDisabled(isFirstDrawer)
      );

      if (!isObjectSaveDisabled) {
        return confirmDiscardChanged(onDiscard);
      }

      onDiscard();
    },
    [dispatch, confirmDiscardChanged]
  );

  const [openDeleteConfirmationModal, deleteConfirmationModal] =
    useDeleteConfirmation();

  const isDrawerOpen = useMemo(() => firstObject != null, [firstObject]);

  const objectType: TType | null = objectTypesMap[objectTypeId] ?? null;

  const onCreate = useCallback(
    (objectTypeId: number) => {
      confirmDiscardChanges(true, () => {
        const objectType = objectTypesMap[objectTypeId];

        const fieldIds = [
          ...(objectType?.parentFields ?? []),
          ...(objectType?.fields ?? []),
        ];

        const fieldIdsToSave = fieldIds
          .filter((fieldId) => isFieldSaveable(fieldMap[fieldId]))
          .sort((idA, idB) => {
            const indexA = globalFieldOrder.findIndex(
              (field) => field.id === idA
            );
            const indexB = globalFieldOrder.findIndex(
              (field) => field.id === idB
            );
            return indexA - indexB;
          });

        dispatch(
          createUnsavedObject({
            typeId: objectTypeId,
            fieldIds: fieldIdsToSave,
          })
        );
      });
    },
    [
      dispatch,
      objectTypesMap,
      fieldMap,
      globalFieldOrder,
      confirmDiscardChanges,
    ]
  );

  const onDuplicate = useCallback(
    (object: TTEObject) => {
      confirmDiscardChanges(true, () => {
        dispatch(duplicateObject(object.id));
      });
    },
    [dispatch, confirmDiscardChanges]
  );

  const onCompare = useCallback(
    (object?: TTEObject) => {
      if (!isDrawerOpen) {
        // If the drawer is closed, set the first object to open the drawer
        dispatch(setFirstObject(object.id));
      } else {
        // If the drawer is open, set the second object for comparison in a second drawer
        confirmDiscardChanges(false, () =>
          dispatch(setSecondObject(object.id))
        );
      }
    },
    [dispatch, isDrawerOpen]
  );

  const onDelete = useCallback(
    (object: TTEObject) =>
      openDeleteConfirmationModal(object.extId, () =>
        dispatch(deleteObject(object.id))
      ),
    [dispatch, openDeleteConfirmationModal]
  );

  const contextMenuActions: ObjectTableProps['contextMenuActions'] = useMemo(
    () => ({
      itemLabel: (item) => item.extId,
      actions: [
        {
          key: 'compare',
          label: getText(
            isDrawerOpen
              ? 'object_manager.object_table.context_menu.compare'
              : 'object_manager.object_table.context_menu.open'
          ),
          icon: <Icon id="EyeOutlined" alt="Eye" />,
          onActionClick: onCompare,
        },
        {
          key: 'duplicate',
          label: getText('object_manager.object_table.context_menu.duplicate'),
          icon: <Icon id="CopyOutlined" alt="Copy" />,
          onActionClick: onDuplicate,
        },
        {
          key: 'delete',
          label: (
            <span style={{ color: 'red' }}>
              {getText('object_manager.object_table.context_menu.delete')}
            </span>
          ),
          icon: (
            <Icon id="DeleteOutlined" alt="Delete" style={{ color: 'red' }} />
          ),
          onActionClick: onDelete,
        },
      ],
    }),
    [isDrawerOpen, onCompare, onDuplicate, onDelete]
  );

  const {
    rowSelection,
    idSelectionMode,
    includedIds: includedObjectIds,
    excludedIds: excludedObjectIds,
    clearSelection,
  } = useTableSelection(objects, undefined);

  useEffect(() => {
    clearSelection();
  }, [objectTypeId, clearSelection]);

  useEffect(() => {
    if (idSelectionMode === 'deselect') {
      setSelectAllTotal((prev) => Math.max(prev, totalCount));
    } else {
      setSelectAllTotal(0);
    }
  }, [idSelectionMode, totalCount]);

  const selectedObjectsCount = useMemo(() => {
    if (idSelectionMode === 'select') {
      return includedObjectIds.length;
    }

    return Math.max(0, selectAllTotal - excludedObjectIds.length);
  }, [excludedObjectIds, idSelectionMode, includedObjectIds, selectAllTotal]);

  const idFilter = useMemo(() => {
    if (idSelectionMode === 'select' && includedObjectIds.length) {
      return { ids: includedObjectIds };
    } else if (idSelectionMode === 'deselect' && excludedObjectIds.length) {
      return {
        excludeIds: excludedObjectIds,
      };
    } else {
      return {};
    }
  }, [idSelectionMode, includedObjectIds, excludedObjectIds]);

  const jobsDisabled =
    selectedObjectsCount === 0 ||
    findObjectPayload.typeId == null ||
    findObjectPayload.typeId?.toString() ===
      allObjectTypes?.[0]?.id?.toString();

  const footerContent: ObjectTableProps['footerContent'] = useMemo(() => {
    const bulkButtons: React.ReactNode[] = [
      <BulkEditStartJobButton
        key="bulk-edit"
        objectType={objectType}
        jobObjectSelection={{
          filter: { ...findObjectPayload, ...idFilter },
        }}
        selectedObjectsCount={selectedObjectsCount}
        disabled={jobsDisabled}
        type="default"
        size="small"
      >
        {getText('edit')}
      </BulkEditStartJobButton>,
    ];

    if (objectExportEnabled && hasExportPermission) {
      bulkButtons.push(
        <ExportObjectsJobButton
          key="export-button"
          objectType={objectType}
          selectedObjectsCount={selectedObjectsCount}
          disabled={jobsDisabled}
          jobObjectSelection={{
            filter: { ...findObjectPayload, ...idFilter },
          }}
        />
      );
    }

    if (hasDeletePermission) {
      bulkButtons.push(
        <BulkDeleteObjectsJobButton
          key="bulk-delete"
          selectedObjectsCount={selectedObjectsCount}
          disabled={jobsDisabled}
          jobObjectSelection={{
            filter: { ...findObjectPayload, ...idFilter },
          }}
        />
      );
    }

    bulkButtons.push(
      <Typography.Text key="selection-count">
        {selectedObjectsCount} selected objects
      </Typography.Text>
    );

    return [
      <ObjectTypeTreePopover key="create-new" onClick={onCreate} onlyLeafNode>
        <Button type="primary" size="small">
          {getText('object_manager.object_table.actions.create_new')}
        </Button>
      </ObjectTypeTreePopover>,
      ...bulkButtons,
    ];
  }, [
    objectType,
    idFilter,
    selectedObjectsCount,
    objectExportEnabled,
    onCreate,
    jobsDisabled,
    findObjectPayload,
    hasExportPermission,
    hasDeletePermission,
  ]);

  const fetchObjects: ObjectTableProps['fetchFn'] = useCallback(
    async (payload) => {
      try {
        dispatch(fetchObjectsRequest());
        const response = await findMoreObjects(payload);
        dispatch(fetchMoreObjectSuccess({ results: response?.results }));
        return response;
      } catch (error) {
        dispatch(fetchObjectsFailure());
        throw error;
      }
    },
    [dispatch]
  );

  const resetFn: ObjectTableProps['resetFn'] = useCallback(() => {
    dispatch(fetchObjectsSuccess({ results: [] }));
  }, [dispatch]);

  const onRow: ObjectTableProps['onRow'] = useCallback(
    (object) => ({
      onClick: () =>
        confirmDiscardChanges(true, () => dispatch(setFirstObject(object.id))),
    }),
    [dispatch]
  );

  const onFilterChange: ObjectTableProps['onFilterChange'] = useCallback(
    (_filters, findObjectsPayload) => {
      // Clear search param if it doesn't match the filter anymore
      setSearchParams(
        (params) => {
          const orgParam = params.get('orgNodeId');
          const typeParam = params.get('typeId');
          const filterOrgs = findObjectsPayload.organizations;
          const orgFilter =
            filterOrgs?.length === 1 ? filterOrgs[0].toString() : null;
          const filterType = findObjectsPayload.typeId;
          if (orgParam && orgParam !== orgFilter) {
            params.delete('orgNodeId');
          }
          if (typeParam && typeParam !== String(filterType)) {
            params.delete('typeId');
          }
          return params;
        },
        { replace: true }
      );

      dispatch(setFindObjectPayload(findObjectsPayload));
    },
    [dispatch, setSearchParams]
  );

  const rowClassName: ObjectTableProps['rowClassName'] = useCallback(
    (record: TTEObject) =>
      firstObject?.id === record.id || secondObject?.id === record.id
        ? 'ant-table-row-selected'
        : '',
    [firstObject, secondObject]
  );

  const handleJobPopoverOpenChange = useCallback(
    (open: boolean) => {
      setIsJobPopoverOpen(open);
      if (open) {
        refetchJobQueueStatus();
      }
    },
    [refetchJobQueueStatus]
  );

  const actions = useMemo(() => {
    return (
      <>
        <ImportObjectsButton objectTypeId={objectTypeId!} />
        <Popover
          trigger="click"
          content={<JobResults />}
          placement="bottomRight"
          destroyOnHidden
          open={isJobPopoverOpen}
          onOpenChange={handleJobPopoverOpenChange}
        >
          <Button
            key="batch_operations"
            icon={
              jobQueueStatus &&
              jobQueueStatus?.running > 0 && <Loading3QuartersOutlined spin />
            }
          >
            {language.batch_operations}
          </Button>
        </Popover>
      </>
    );
  }, [
    handleJobPopoverOpenChange,
    jobQueueStatus?.running,
    isJobPopoverOpen,
    objectTypeId,
  ]);

  return (
    <DefaultContentLayout
      title="Objects"
      subtitle="Create, edit and delete objects in the database"
      actions={actions}
    >
      <div className="object-manager inner-content--wrapper">
        <ObjectTable
          tableId="objects.management"
          objects={objects}
          contextMenuActions={contextMenuActions}
          footerContent={footerContent}
          onRow={onRow}
          fetchFn={fetchObjects}
          resetFn={resetFn}
          loading={isLoadingObjects || isLoadingFields || isLoadingObjectTypes}
          rowClassName={rowClassName}
          onFilterChange={onFilterChange}
          allowTemplateObjects
          defaultFilter={defaultFilter}
          rowSelection={rowSelection}
          onFilteredTotalChanged={setTotalCount}
          showReloadAlert={showReloadAlert}
          onDismissReloadAlert={() => setShowReloadAlert(false)}
        />
        {deleteConfirmationModal}
        <ObjectManagerDrawer confirmDiscardChanges={confirmDiscardChanges} />
        <DiscardConfirmation
          open={isDiscardDialogOpen}
          onClose={onCloseDiscardDialog}
          confirmFn={onConfirmDiscardDialog}
        />
      </div>
    </DefaultContentLayout>
  );
};

export default ObjectManagerPage;
